import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import { MAX_MESSAGE_LENGTH, type ApplicationCommand, type Gif } from '@comms/shared';
import { GifPicker } from './GifPicker';
import { CommandForm, CommandPicker } from './CommandInput';
import { displayName, useStore } from '../store';
import { EmojiPicker, type ViewMessage } from './MessageList';
import { ErrorText, Icon, Spinner, cx } from './ui';
import { shrinkImage } from '../lib/images';

export interface MentionCandidate {
  id: string;
  label: string;
  sub?: string;
  insert: string;
}

export function Composer({
  placeholder,
  disabled,
  disabledReason,
  replyTo,
  onCancelReply,
  onSend,
  onTyping,
  mentionCandidates,
  canAttach = true,
  maxFileSize,
  guildId,
  stickers,
  onSendSticker,
  onCreatePoll,
  onSendGif,
  commands,
  onCommandsWanted,
  onRunCommand,
}: {
  placeholder: string;
  disabled?: boolean;
  disabledReason?: string;
  replyTo?: ViewMessage | null;
  onCancelReply?: () => void;
  onSend: (content: string, files: File[]) => Promise<void>;
  onTyping?: () => void;
  mentionCandidates?: (query: string) => MentionCandidate[];
  canAttach?: boolean;
  maxFileSize?: number;
  guildId?: string | null;
  stickers?: { id: string; name: string; url: string }[];
  onSendSticker?: (id: string) => Promise<void>;
  onCreatePoll?: () => void;
  onSendGif?: (gif: Gif) => Promise<void>;
  /** slash commands available here (undefined while loading or when unavailable) */
  commands?: ApplicationCommand[];
  /** the user started typing a command: load the list */
  onCommandsWanted?: () => void;
  onRunCommand?: (cmd: ApplicationCommand, options: Record<string, string>) => Promise<void>;
}) {
  const gifsEnabled = useStore((s) => s.gifsEnabled);
  const [gifPicker, setGifPicker] = useState(false);
  const [stickerPicker, setStickerPicker] = useState(false);
  const [text, setText] = useState('');
  const [files, setFilesState] = useState<File[]>([]);
  // Mirrors `files` synchronously, and tracks photos still being shrunk, so a send right
  // after picking a file waits for it instead of going out without it.
  const filesRef = useRef<File[]>([]);
  const adding = useRef<Promise<void>>(Promise.resolve());
  const [processing, setProcessing] = useState(0);
  const setFiles = (next: File[]) => {
    filesRef.current = next;
    setFilesState(next);
  };
  const [error, setError] = useState<unknown>(null);
  const [sending, setSending] = useState(false);
  const [picker, setPicker] = useState(false);
  const [mention, setMention] = useState<{ query: string; start: number; index: number } | null>(
    null,
  );
  const [cmdIndex, setCmdIndex] = useState(0);
  const [activeCmd, setActiveCmd] = useState<ApplicationCommand | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);
  const replyName = useStore((s) => (replyTo ? displayName(s, replyTo.authorId) : ''));

  useEffect(() => {
    if (replyTo) ref.current?.focus();
  }, [replyTo]);

  const candidates =
    mention && mentionCandidates ? mentionCandidates(mention.query).slice(0, 8) : [];
  const cmdQuery = onRunCommand ? /^\/([a-z0-9_-]*)$/.exec(text)?.[1] : undefined;
  const cmdMatches =
    cmdQuery !== undefined && commands
      ? commands.filter((c) => c.name.startsWith(cmdQuery)).slice(0, 25)
      : [];

  function pickCommand(c: ApplicationCommand) {
    setActiveCmd(c);
    setText('');
    setCmdIndex(0);
    setError(null);
  }
  function cancelCommand() {
    setActiveCmd(null);
    requestAnimationFrame(() => ref.current?.focus());
  }

  function addFiles(list: FileList | File[]) {
    const picked = Array.from(list);
    const prev = adding.current;
    setProcessing((n) => n + 1);
    adding.current = (async () => {
      // Large photos are shrunk right away, so the size limit applies to what gets sent.
      const arr = await Promise.all(picked.map(shrinkImage));
      await prev;
      const tooBig = maxFileSize ? arr.find((f) => f.size > maxFileSize) : undefined;
      if (tooBig) {
        setError(new Error(`${tooBig.name} is too large`));
        return;
      }
      setFiles([...filesRef.current, ...arr].slice(0, 10));
    })().finally(() => setProcessing((n) => n - 1));
  }

  async function send() {
    const content = text.trim();
    if ((!content && files.length === 0 && !processing) || sending) return;
    if (content.length > MAX_MESSAGE_LENGTH) {
      setError(new Error(`Messages can be at most ${MAX_MESSAGE_LENGTH} characters`));
      return;
    }
    setSending(true);
    setError(null);
    try {
      await adding.current;
      if (!content && filesRef.current.length === 0) return;
      await onSend(content, filesRef.current);
      setText('');
      setFiles([]);
    } catch (err) {
      setError(err);
    } finally {
      setSending(false);
      ref.current?.focus();
    }
  }

  function updateMention(value: string, caret: number) {
    const m = /(^|\s)@([\w.]{0,32})$/.exec(value.slice(0, caret));
    setMention(
      m ? { query: m[2]!.toLowerCase(), start: caret - m[2]!.length - 1, index: 0 } : null,
    );
  }

  function pickMention(c: MentionCandidate) {
    if (!mention) return;
    const caret = ref.current?.selectionStart ?? text.length;
    const next = `${text.slice(0, mention.start)}${c.insert} ${text.slice(caret)}`;
    setText(next);
    setMention(null);
    requestAnimationFrame(() => ref.current?.focus());
  }

  /** Enter or the send button: pick the highlighted command, or send. */
  function submit() {
    if (cmdMatches.length > 0)
      return pickCommand(cmdMatches[Math.min(cmdIndex, cmdMatches.length - 1)]!);
    void send();
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (cmdMatches.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        setCmdIndex((cmdIndex + delta + cmdMatches.length) % cmdMatches.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickCommand(cmdMatches[Math.min(cmdIndex, cmdMatches.length - 1)]!);
        return;
      }
    }
    if (candidates.length > 0 && mention) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        setMention({
          ...mention,
          index: (mention.index + delta + candidates.length) % candidates.length,
        });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickMention(candidates[mention.index]!);
        return;
      }
      if (e.key === 'Escape') return setMention(null);
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape' && replyTo) {
      onCancelReply?.();
    }
  }

  function onChange(value: string, caret: number) {
    setText(value);
    setCmdIndex(0);
    if (value.startsWith('/')) onCommandsWanted?.();
    updateMention(value, caret);
    if (onTyping && value && Date.now() - lastTyping.current > 8000) {
      lastTyping.current = Date.now();
      onTyping();
    }
  }

  if (disabled) {
    return (
      <div className="mx-4 mb-6 rounded-lg bg-bg-3 px-4 py-3 text-sm text-text-3">
        {disabledReason ?? 'You cannot send messages here.'}
      </div>
    );
  }

  return (
    <div
      className="relative mx-4 mb-6"
      onDragOver={(e: DragEvent) => canAttach && e.preventDefault()}
      onDrop={(e: DragEvent) => {
        if (!canAttach) return;
        e.preventDefault();
        addFiles(e.dataTransfer.files);
      }}
    >
      {cmdMatches.length > 0 && !activeCmd && (
        <CommandPicker
          matches={cmdMatches}
          index={Math.min(cmdIndex, cmdMatches.length - 1)}
          onPick={pickCommand}
        />
      )}
      {candidates.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-2 rounded bg-bg-1 p-1 shadow-xl">
          {candidates.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => (e.preventDefault(), pickMention(c))}
              className={cx(
                'flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm',
                i === mention!.index ? 'bg-bg-active text-text-0' : 'text-text-2',
              )}
            >
              {c.label}
              {c.sub && <span className="text-text-3">{c.sub}</span>}
            </button>
          ))}
        </div>
      )}
      {replyTo && (
        <div className="flex items-center justify-between rounded-t-lg bg-bg-1 px-4 py-2 text-sm text-text-2">
          <span>
            Replying to <span className="font-semibold text-text-1">{replyName}</span>
          </span>
          <button type="button" onClick={onCancelReply} className="text-text-3 hover:text-text-0">
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 bg-bg-3 p-3">
          {files.map((f, i) => (
            <div
              key={i}
              className="relative flex w-36 flex-col items-center rounded bg-bg-1 p-2 text-xs text-text-2"
            >
              {f.type.startsWith('image/') ? (
                <img
                  src={URL.createObjectURL(f)}
                  alt=""
                  className="h-20 w-full rounded object-cover"
                />
              ) : (
                <Icon name="paperclip" className="h-10 w-10" />
              )}
              <span className="mt-1 w-full truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
                className="absolute -right-2 -top-2 rounded-full bg-danger p-0.5 text-white"
              >
                <Icon name="x" className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {activeCmd && guildId && onRunCommand ? (
        <div className="flex items-center gap-2 rounded-lg bg-bg-3 px-4 py-2">
          <CommandForm
            cmd={activeCmd}
            guildId={guildId}
            onCancel={cancelCommand}
            onRun={async (options) => {
              setError(null);
              try {
                await onRunCommand(activeCmd, options);
                cancelCommand();
              } catch (err) {
                setError(err);
              }
            }}
          />
        </div>
      ) : (
        <div
          className={cx(
            'flex items-end gap-2 bg-bg-3 px-4 py-2.5',
            replyTo || files.length ? 'rounded-b-lg' : 'rounded-lg',
          )}
        >
          {canAttach && (
            <>
              <button
                type="button"
                title="Upload a file"
                onClick={() => fileInput.current?.click()}
                className="pb-0.5 text-text-2 hover:text-text-0"
              >
                <Icon name="plus" className="h-6 w-6 rounded-full bg-text-2 p-1 text-bg-3" />
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => (
                  e.target.files && addFiles(e.target.files),
                  (e.target.value = '')
                )}
              />
            </>
          )}
          <textarea
            ref={ref}
            value={text}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value, e.target.selectionStart)}
            onKeyDown={onKeyDown}
            onPaste={(e: ClipboardEvent) =>
              canAttach &&
              e.clipboardData.files.length > 0 &&
              (e.preventDefault(), addFiles(e.clipboardData.files))
            }
            rows={1}
            maxLength={MAX_MESSAGE_LENGTH + 100}
            className="max-h-60 min-h-6 flex-1 resize-none bg-transparent py-0.5 text-text-1 outline-none placeholder:text-text-3"
            style={{ height: `${Math.min(240, 24 * Math.max(1, text.split('\n').length))}px` }}
          />
          {onSendGif && gifsEnabled && (
            <div className="relative">
              <button
                type="button"
                title="GIFs"
                onClick={(e) => (e.stopPropagation(), setGifPicker(!gifPicker))}
                className="rounded border border-text-3 px-1 pb-0 text-[11px] font-bold leading-4 text-text-2 hover:border-text-0 hover:text-text-0"
              >
                GIF
              </button>
              {gifPicker && (
                <GifPicker
                  onClose={() => setGifPicker(false)}
                  onPick={(g) => {
                    setGifPicker(false);
                    onSendGif(g).catch(setError);
                  }}
                />
              )}
            </div>
          )}
          {onCreatePoll && (
            <button
              type="button"
              title="Create poll"
              onClick={onCreatePoll}
              className="pb-0.5 text-text-2 hover:text-text-0"
            >
              <Icon name="poll" className="h-6 w-6" />
            </button>
          )}
          {onSendSticker && !!stickers?.length && (
            <div className="relative">
              <button
                type="button"
                title="Stickers"
                onClick={(e) => (e.stopPropagation(), setStickerPicker(!stickerPicker))}
                className="pb-0.5 text-text-2 hover:text-text-0"
              >
                <Icon name="sticker" className="h-6 w-6" />
              </button>
              {stickerPicker && (
                <StickerPicker
                  stickers={stickers}
                  onClose={() => setStickerPicker(false)}
                  onPick={(id) => {
                    setStickerPicker(false);
                    onSendSticker(id).catch(setError);
                  }}
                />
              )}
            </div>
          )}
          <div className="relative">
            <button
              type="button"
              title="Emoji"
              onClick={(e) => (e.stopPropagation(), setPicker(!picker))}
              className="pb-0.5 text-text-2 hover:text-text-0"
            >
              <Icon name="smile" className="h-6 w-6" />
            </button>
            {picker && (
              <div className="absolute bottom-10 right-0">
                <EmojiPicker
                  guildId={guildId}
                  onClose={() => setPicker(false)}
                  onPick={(e) => (setText((t) => t + e), setPicker(false))}
                />
              </div>
            )}
          </div>
          <button
            type="button"
            aria-label="Send message"
            title="Send (Enter)"
            disabled={(!text.trim() && files.length === 0 && !processing) || sending}
            onClick={submit}
            className="-my-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-white transition hover:bg-accent-hover disabled:bg-transparent disabled:text-text-3"
          >
            {sending || processing > 0 ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <Icon name="send" className="h-4 w-4 -translate-x-px translate-y-px" />
            )}
          </button>
        </div>
      )}
      <ErrorText error={error} />
    </div>
  );
}

function StickerPicker({
  stickers,
  onPick,
  onClose,
}: {
  stickers: { id: string; name: string; url: string }[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const t = window.setTimeout(() => window.addEventListener('click', onClose, { once: true }), 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('click', onClose);
    };
  }, [onClose]);
  return (
    <div
      className="absolute bottom-10 right-0 z-30 grid max-h-80 w-80 grid-cols-3 gap-2 overflow-y-auto rounded bg-bg-0 p-2 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      {stickers.map((s) => (
        <button
          key={s.id}
          type="button"
          title={s.name}
          onClick={() => onPick(s.id)}
          className="rounded p-1 hover:bg-bg-hover"
        >
          <img src={s.url} alt={s.name} className="h-20 w-20 object-contain" />
        </button>
      ))}
    </div>
  );
}
